import { useId, useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "next-i18next";
import { EmptyState } from "src/components-mui/common/empty-state/empty-state";
import { HideContent } from "src/components-mui/common/hide-content/hide-content";
import { Container } from "src/components-mui/layout/container";
import { ListMotiveTiles } from "src/components-mui/modules/underlying-lists-module/components/list-motive-tiles";
import { ListsGrid } from "src/components-mui/modules/underlying-lists-module/components/lists-grid";
import { ListsHeader } from "src/components-mui/modules/underlying-lists-module/components/lists-header";
import { useUnderlyingListsModule } from "src/components-mui/modules/underlying-lists-module/hooks/use-underlying-lists-module";
import { UnderlyingListMotive } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.constants";
import { IUnderlyingListModuleProps } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.types";

export const UnderlyingListModule = ({
    title,
    titleAsH1,
    description,
    overlayTitle,
    overlayDescription,
    ...dataProps
}: IUnderlyingListModuleProps) => {
    const { t } = useTranslation("underlying-overview");
    const listIdPrefix = useId();
    const { daysCount, setDaysCount, selectedMotive, setSelectedMotive, lists, shouldShowOverlay } = useUnderlyingListsModule(dataProps);
    const getListId = ({ motive }: { motive: UnderlyingListMotive }) => `${listIdPrefix}-${motive}`;
    const overlay = useMemo(
        () =>
            shouldShowOverlay ? (
                <HideContent
                    title={overlayTitle}
                    description={overlayDescription}
                    titleComponent="h4"
                    paperSx={{ width: { xs: "100%", md: "537px", lg: "649px" } }}
                />
            ) : null,
        [overlayDescription, overlayTitle, shouldShowOverlay]
    );

    return (
        <Container>
            <ListsHeader title={title} titleAsH1={titleAsH1} description={description} daysCount={daysCount} onDaysCountChange={setDaysCount} />
            {lists ? (
                <Box sx={{ mt: { xs: 2, sm: 4 } }}>
                    <ListMotiveTiles lists={lists} selectedMotive={selectedMotive} getListId={getListId} onSelect={setSelectedMotive} />
                    <ListsGrid lists={lists} daysCount={daysCount} selectedMotive={selectedMotive} listIdPrefix={listIdPrefix} overlay={overlay} />
                </Box>
            ) : (
                <EmptyState title={t("error.heading")} description={t("error.description")} sx={{ mt: 4 }} />
            )}
        </Container>
    );
};
